import { useGameplay } from '@sorrem-client/ui/features/PlayPage/gameplay-context';
import { useCameraZoom } from '@sorrem-client/ui/hooks';
import { useEffect } from 'react';

/** How long the wheel rests before its zoom is saved, so a spin is one account update, not one per notch. */
const WHEEL_SAVE_DELAY_MS = 500;

/**
 * Hands the account's camera zoom to the game, and saves the zoom the mouse
 * wheel turns to: shown at once, saved once the wheel rests (or the run
 * ends first). A locked zoom ignores the wheel (`GameStateBridge`).
 */
export function useSyncCameraZoom(): void {
    const { bridge } = useGameplay();
    const { cameraZoom, previewLevel, setLevel } = useCameraZoom();
    const { level, isLocked } = cameraZoom;

    useEffect(() => {
        bridge.setCameraZoom({ level, isLocked });
    }, [bridge, level, isLocked]);

    useEffect(() => {
        let pendingLevel: number | null = null;
        let timer: ReturnType<typeof setTimeout> | undefined;
        const save = (): void => {
            if (pendingLevel !== null) {
                setLevel(pendingLevel);
                pendingLevel = null;
            }
        };
        const unsubscribe = bridge.subscribeCameraZoomWheel((wheeled) => {
            previewLevel(wheeled);
            pendingLevel = wheeled;
            clearTimeout(timer);
            timer = setTimeout(save, WHEEL_SAVE_DELAY_MS);
        });
        return () => {
            unsubscribe();
            clearTimeout(timer);
            save();
        };
    }, [bridge, previewLevel, setLevel]);
}
